import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  FaUpload, FaTrash, FaDownload, FaFileAlt, FaFilePdf, FaFileImage, FaTimes, FaMobileAlt, FaHospital, FaEye,
} from 'react-icons/fa';
import CustomSpinner from '../../../Components/CommonComponents/CustomSpinner';
import ModalPortal, { MODAL_Z } from '../../../Components/CommonComponents/ModalPortal';
import ConfirmDialog from '../../../Components/CommonComponents/ConfirmDialog';
import CameraCapture from '../../../Components/CommonComponents/CameraCapture';
import {
  fetchDocuments,
  uploadDocuments,
  deleteDocument,
  downloadDocument,
  fetchDocumentObjectUrl,
} from '../../../Services/patientProfile';

const CATEGORY_OPTIONS = [
  { value: 'document', label: 'Document' },
  { value: 'report', label: 'Report' },
  { value: 'prescription', label: 'Prescription' },
  { value: 'picture', label: 'Picture' },
  { value: 'other', label: 'Other' },
];

const fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (value) =>
  new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const iconFor = (mimetype) => {
  if (String(mimetype).startsWith('image/')) return <FaFileImage className="text-violet-500" />;
  if (String(mimetype).includes('pdf')) return <FaFilePdf className="text-rose-500" />;
  return <FaFileAlt className="text-slate-400" />;
};

/**
 * File routes sit behind the staff JWT, which an <img src> cannot carry, so
 * thumbnails are fetched as blobs and revoked on unmount.
 */
const Thumbnail = ({ documentId, alt, onOpen }) => {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let objectUrl;
    let cancelled = false;

    fetchDocumentObjectUrl(documentId)
      .then((u) => {
        if (cancelled) return URL.revokeObjectURL(u);
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [documentId]);

  if (!url) return <div className="h-40 w-full animate-pulse rounded-lg bg-slate-100" />;

  return (
    <button type="button" onClick={() => onOpen(url)} className="block w-full">
      <img src={url} alt={alt} className="h-40 w-full rounded-lg object-cover transition hover:opacity-90" />
    </button>
  );
};

export default function FilesTab({ patientId, mode = 'documents', onChanged }) {
  const [documents, setDocuments] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [category, setCategory] = useState(mode === 'pictures' ? 'picture' : 'document');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async () => {
    try {
      setDocuments(await fetchDocuments(patientId));
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not load files');
      setDocuments([]);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    if (!documents) return null;
    return mode === 'pictures'
      ? documents.filter((d) => d.category === 'picture')
      : documents.filter((d) => d.category !== 'picture');
  }, [documents, mode]);

  const submit = async () => {
    if (!selected.length) return toast.error('Choose at least one file');
    setUploading(true);
    try {
      await uploadDocuments(patientId, selected, { category, title, notes });
      toast.success(`${selected.length} file(s) uploaded`);
      setSelected([]);
      setTitle('');
      setNotes('');
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteDocument(pendingDelete._id);
      toast.success('File deleted');
      setPendingDelete(null);
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not delete the file');
    }
  };

  const openInTab = async (doc) => {
    try {
      const url = await fetchDocumentObjectUrl(doc._id);
      window.open(url, '_blank', 'noopener');
      // The tab holds its own reference; release ours once it has loaded.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error('Could not open the file');
    }
  };

  if (visible === null) return <CustomSpinner />;

  return (
    <div className="space-y-5">
      {/* --------------------------------- upload -------------------------------- */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="font-semibold text-slate-800">
          {mode === 'pictures' ? 'Upload pictures' : 'Upload documents'}
        </h3>
        <p className="text-xs text-slate-500">
          {mode === 'pictures'
            ? 'Clinical photographs, scans and any other images for this patient.'
            : 'Reports, referral letters, discharge summaries and other records.'}
        </p>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-12">
          <div className="md:col-span-5">
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">Files</label>
            <input
              type="file"
              multiple
              accept={mode === 'pictures' ? 'image/*' : undefined}
              onChange={(e) => setSelected(Array.from(e.target.files || []))}
              className="mt-1 block w-full text-sm text-slate-500 file:mr-3 file:rounded-lg file:border-0 file:bg-primary-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-primary-700 hover:file:bg-primary-100"
            />
            <CameraCapture className="mt-2" onCapture={(file) => setSelected((prev) => [...prev, file])} />
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">Category</label>
            <select
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-4">
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Title {selected.length > 1 && <span className="normal-case text-slate-400">(single file only)</span>}
            </label>
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={title}
              disabled={selected.length > 1}
              placeholder="e.g. Blood report — March"
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="md:col-span-12">
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">Notes</label>
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        {!!selected.length && (
          <div className="mt-3 flex flex-wrap gap-2">
            {selected.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">
                {f.name} <span className="text-slate-400">{fmtSize(f.size)}</span>
                <button onClick={() => setSelected((s) => s.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-rose-600">
                  <FaTimes />
                </button>
              </span>
            ))}
          </div>
        )}

        <button
          onClick={submit}
          disabled={uploading || !selected.length}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600 disabled:opacity-50"
        >
          <FaUpload /> {uploading ? 'Uploading…' : 'Upload'}
        </button>
      </div>

      {/* --------------------------------- listing ------------------------------- */}
      {!visible.length ? (
        <p className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
          Nothing here yet.
        </p>
      ) : mode === 'pictures' ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {visible.map((doc) => (
            <div key={doc._id} className="rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
              <Thumbnail documentId={doc._id} alt={doc.originalname} onOpen={setLightbox} />
              <div className="px-1 pb-1 pt-2">
                <p className="truncate text-xs font-medium text-slate-800">{doc.title || doc.originalname}</p>
                <p className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-500">
                  {doc.source === 'portal' ? <FaMobileAlt /> : <FaHospital />}
                  {fmtDate(doc.createdAt)}
                </p>
                <div className="mt-2 flex gap-1">
                  <button
                    onClick={() => downloadDocument(doc._id, doc.originalname)}
                    className="rounded p-1.5 text-slate-500 hover:bg-slate-100"
                    title="Download"
                  >
                    <FaDownload />
                  </button>
                  <button
                    onClick={() => setPendingDelete(doc)}
                    className="rounded p-1.5 text-rose-400 hover:bg-rose-50"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5 font-semibold">File</th>
                <th className="px-4 py-2.5 font-semibold">Category</th>
                <th className="px-4 py-2.5 font-semibold">Added by</th>
                <th className="px-4 py-2.5 font-semibold">Date</th>
                <th className="px-4 py-2.5 font-semibold">Size</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map((doc) => (
                <tr key={doc._id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {iconFor(doc.mimetype)}
                      <div className="min-w-0">
                        <p className="truncate font-medium text-slate-800">{doc.title || doc.originalname}</p>
                        {doc.notes && <p className="truncate text-xs text-slate-500">{doc.notes}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium capitalize text-slate-600">
                      {doc.category}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 text-xs font-medium ${
                        doc.source === 'portal' ? 'text-violet-700' : 'text-slate-600'
                      }`}
                    >
                      {doc.source === 'portal' ? <FaMobileAlt /> : <FaHospital />}
                      {doc.source === 'portal' ? 'Patient' : doc.uploadedBy?.name || 'Clinic'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-600">{fmtDate(doc.createdAt)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtSize(doc.size)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <button onClick={() => openInTab(doc)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" title="View">
                      <FaEye />
                    </button>
                    <button
                      onClick={() => downloadDocument(doc._id, doc.originalname)}
                      className="rounded p-1.5 text-slate-500 hover:bg-slate-100"
                      title="Download"
                    >
                      <FaDownload />
                    </button>
                    <button
                      onClick={() => setPendingDelete(doc)}
                      className="rounded p-1.5 text-rose-400 hover:bg-rose-50"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lightbox && (
        <ModalPortal isOpen={!!lightbox} onClose={() => setLightbox(null)}>
        <div className={`fixed inset-0 ${MODAL_Z} flex items-center justify-center bg-slate-950/80 p-6`} onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" className="max-h-full max-w-full rounded-lg object-contain" />
          <button className="absolute right-6 top-6 text-2xl text-white/80 hover:text-white">
            <FaTimes />
          </button>
        </div></ModalPortal>
      )}

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Delete this file?"
        message={pendingDelete ? `"${pendingDelete.title || pendingDelete.originalname}" will be permanently removed.` : ''}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
